import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FoBars, FoCard, FoEmpty, FoKpis, FoTable, formatEgp, statusBadge } from "./financeUi";
import { downloadOs, os } from "./osClient";
import { useOsResource } from "./osForm";
import { formatStamp } from "./osRules";

const splitLabels = [
  ["payroll", "Payroll"],
  ["rent", "Rent"],
  ["shipping", "Shipping"],
  ["utilities", "Utilities"],
  ["technology", "Technology"],
  ["other", "Other"],
];

export default function OsDashboardWidgets({ startDate, endDate }) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState("");
  const view = useOsResource(
    (auth) => os.dashboard(auth, { startDate, endDate }),
    [startDate, endDate]
  );
  const data = view.data;
  if (data?.subscriptionType === "External") return null;
  const series = data?.revenueVsExpenses || [];
  const split = data?.expenseSplit || {};
  const maxSplit = Math.max(1, ...splitLabels.map(([key]) => Number(split[key]) || 0));
  const cod = data?.codReconciliation || [];
  const activity = data?.recentActivity || [];

  const download = async (href, name) => {
    if (!href) return;
    setDownloading(name);
    try {
      await downloadOs(view.token, href, undefined, name);
    } catch (err) {
      /* toast already shown */
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="fo-stack">
      <FoKpis
        items={[
          { label: "Period change", value: data?.periodChange?.label || "—", note: data?.periodChange ? `${formatEgp(data.periodChange.current)} vs ${formatEgp(data.periodChange.previous)}` : "Same-length previous window" },
          { label: "Accounts receivable", value: `${formatEgp(data?.accountsReceivable?.total)} EGP`, note: `${data?.accountsReceivable?.overdueBrandCount || 0} overdue brands` },
          { label: "Invoices", value: data?.invoices?.pendingCount || 0, note: `${formatEgp(data?.invoices?.outstandingAmount)} EGP outstanding` },
          { label: "Cash on hand", value: `${formatEgp(data?.cashOnHand)} EGP` },
          { label: "Bank balance", value: `${formatEgp(data?.bankBalance)} EGP`, note: data?.unreconciledCount ? `${data.unreconciledCount} unreconciled` : formatStamp(data?.lastReconciledAt) },
          { label: "Subscription MRR", value: `${formatEgp(data?.subscriptionMrr)} EGP`, note: "Active memberships" },
        ]}
      />

      <FoCard
        title="Revenue vs expenses"
        note="Six months ending in the selected range. Downloads are the profit and loss export for that chart."
        actions={(
          <div className="fo-tier-acts">
            <button type="button" className="fo-btn" disabled={!data?.pnlExport?.pdf || downloading !== ""} onClick={() => download(data.pnlExport.pdf, "profit-and-loss.pdf")}>PDF</button>
            <button type="button" className="fo-btn" disabled={!data?.pnlExport?.xlsx || downloading !== ""} onClick={() => download(data.pnlExport.xlsx, "profit-and-loss.xlsx")}>Spreadsheet</button>
          </div>
        )}
      >
        {view.loading && <FoEmpty>Loading the finance overview…</FoEmpty>}
        {!view.loading && (
          <>
            <div className="fo-legend"><span><i /> Revenue</span><span><i className="exp" /> Expenses</span></div>
            <FoBars months={series} />
          </>
        )}
      </FoCard>

      <div className="fo-split">
        <FoCard title="Expense split" note={split.carrierShippingFees ? `Carrier shipping fees ${formatEgp(data?.carrierShippingFees ?? split.carrierShippingFees)} EGP. Shipping in the split uses the expense ledger when that category is non-zero.` : "Company expense ledger for this range."}>
          {splitLabels.map(([key, label]) => (
            <div key={key}>
              <div className="fo-meter">
                <span style={{ width: "auto", textAlign: "left", color: "var(--tx)" }}>{label}</span>
                <b>{formatEgp(split[key])} EGP</b>
              </div>
              <div className="fo-track"><i style={{ width: `${((Number(split[key]) || 0) / maxSplit) * 100}%` }} /></div>
            </div>
          ))}
        </FoCard>
        <FoCard title="Recent activity">
          {activity.length === 0 && <FoEmpty>No financial activity yet.</FoEmpty>}
          {activity.map((item) => (
            <button
              key={item.id}
              type="button"
              className="fo-activity"
              onClick={() => navigate(activityPath(item))}
            >
              <span>{statusBadge(item.kind)}</span>
              <span>
                <strong>{item.title}</strong>
                <em>{item.actor} · {formatStamp(item.at)}</em>
              </span>
            </button>
          ))}
        </FoCard>
      </div>

      <FoCard title="COD reconciliation" note="A delivered COD order with no COD transaction is missing. A short payment is partial. Unlinked COD transactions are reconciled on their own.">
        <FoTable
          pagination={false}
          rowKey={(row) => row.courier}
          dataSource={cod}
          empty="No COD couriers in this range."
          columns={[
            { title: "Courier", dataIndex: "courier" },
            { title: "Collected", dataIndex: "collectedAmount", render: (value) => formatEgp(value) },
            { title: "Reconciled", dataIndex: "reconciledAmount", render: (value) => formatEgp(value) },
            { title: "Partial", dataIndex: "partialAmount", render: (value) => formatEgp(value) },
            { title: "Missing", dataIndex: "missingAmount", render: (value) => formatEgp(value) },
          ]}
        />
      </FoCard>
    </div>
  );
}

function activityPath(item) {
  const type = String(item.entityType || item.kind || "").toLowerCase();
  if (type.includes("invoice")) return "/warehousing/finance/invoice-register";
  if (type.includes("compensation") || type.includes("fee")) return "/warehousing/finance/fees-compensations";
  if (type.includes("reconcil") || type.includes("cash")) return "/warehousing/finance/cash-bank";
  if (type.includes("due") || type.includes("reminder")) return "/warehousing/finance/payouts/due-board";
  if (type.includes("payout")) return "/warehousing/finance/payouts/workflow";
  return "/warehousing/finance/audit";
}
